import { useEffect, useMemo, useRef, useState } from "react";
import { useAdminProducts, useAdminDeals } from "@/hooks/useAdminStore";
import { useAdminMarketplaces, type AdminMarketplace, type MarketplaceEntryType } from "@/hooks/useAdminMarketplaces";
import AdminHeader from "@/pages/admin/components/AdminHeader";
import ConfirmDialog from "@/pages/admin/components/ConfirmDialog";
import { clearDraft, readDraft, writeDraft } from "@/utils/adminDraft";

type MarketplaceForm = {
  name: string;
  slug: string;
  website: string;
  active: boolean;
  entryType: MarketplaceEntryType;
};

type MarketplaceModalState = {
  isOpen: boolean;
  mode: "add" | "edit";
  marketplaceId: number | null;
  form: MarketplaceForm;
};

const emptyForm: MarketplaceForm = {
  name: "",
  slug: "",
  website: "",
  active: true,
  entryType: "marketplace",
};

const MARKETPLACE_DRAFT_STORAGE_KEY = "afiliabr_admin_marketplace_draft_v1";
const MARKETPLACE_MODAL_STATE_STORAGE_KEY = "afiliabr_admin_marketplace_modal_state_v1";

function hasMeaningfulMarketplaceDraft(form: MarketplaceForm) {
  return Boolean(
    form.name.trim() ||
    form.slug.trim() ||
    form.website.trim() ||
    form.active !== emptyForm.active ||
    form.entryType !== emptyForm.entryType
  );
}

function readStoredMarketplaceDraft() {
  return readDraft<MarketplaceForm>(MARKETPLACE_DRAFT_STORAGE_KEY);
}

function readStoredMarketplaceModalState() {
  return readDraft<MarketplaceModalState>(MARKETPLACE_MODAL_STATE_STORAGE_KEY);
}

function hasStoredMarketplaceDraft() {
  const draft = readStoredMarketplaceDraft();
  return Boolean(draft && hasMeaningfulMarketplaceDraft(draft));
}

function clearStoredMarketplaceDraft() {
  clearDraft(MARKETPLACE_DRAFT_STORAGE_KEY);
}

function clearStoredMarketplaceModalState() {
  clearDraft(MARKETPLACE_MODAL_STATE_STORAGE_KEY);
}

function getEntryTypeLabel(type: MarketplaceEntryType) {
  return type === "official_store" ? "Loja Oficial" : "Marketplace";
}

export default function AdminMarketplaces() {
  const { products } = useAdminProducts();
  const { deals } = useAdminDeals();
  const {
    marketplaces,
    addMarketplace,
    updateMarketplace,
    deleteMarketplace,
    toggleActive,
    resetToDefault,
    slugify,
  } = useAdminMarketplaces();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingMarketplace, setEditingMarketplace] = useState<AdminMarketplace | null>(null);
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [form, setForm] = useState<MarketplaceForm>(readStoredMarketplaceDraft() ?? emptyForm);
  const [toast, setToast] = useState<string | null>(null);
  const [restoredModalOnPageLoad, setRestoredModalOnPageLoad] = useState(false);
  const backdropPointerStarted = useRef(false);

  const usageByMarketplace = useMemo(() => {
    return [...products, ...deals].reduce<Record<string, number>>((acc, item) => {
      acc[item.marketplace] = (acc[item.marketplace] || 0) + 1;
      return acc;
    }, {});
  }, [deals, products]);

  const usageByOfficialStore = useMemo(() => {
    return products.reduce<Record<string, number>>((acc, item) => {
      if (!item.officialStoreName) return acc;
      acc[item.officialStoreName] = (acc[item.officialStoreName] || 0) + 1;
      return acc;
    }, {});
  }, [products]);

  const activeMarketplacesCount = marketplaces.filter((marketplace) => marketplace.active && marketplace.entryType === "marketplace").length;
  const activeOfficialStoresCount = marketplaces.filter((marketplace) => marketplace.active && marketplace.entryType === "official_store").length;

  const showToast = (message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    if (restoredModalOnPageLoad || modalOpen) return;

    const savedState = readStoredMarketplaceModalState();
    if (!savedState?.isOpen) return;

    if (savedState.mode === "edit") {
      const marketplace = marketplaces.find((item) => item.id === savedState.marketplaceId);
      if (!marketplace) {
        clearStoredMarketplaceModalState();
        return;
      }

      setEditingMarketplace(marketplace);
    } else {
      setEditingMarketplace(null);
    }

    setForm(savedState.form);
    setModalOpen(true);
    setRestoredModalOnPageLoad(true);
    showToast(savedState.mode === "edit" ? "Edição de cadastro restaurada automaticamente." : "Cadastro em andamento restaurado automaticamente.");
  }, [marketplaces, modalOpen, restoredModalOnPageLoad]);

  useEffect(() => {
    if (editingMarketplace) return;

    writeDraft(MARKETPLACE_DRAFT_STORAGE_KEY, form, hasMeaningfulMarketplaceDraft);
  }, [editingMarketplace, form]);

  useEffect(() => {
    if (!modalOpen) return;

    writeDraft(
      MARKETPLACE_MODAL_STATE_STORAGE_KEY,
      {
        isOpen: true,
        mode: editingMarketplace ? "edit" : "add",
        marketplaceId: editingMarketplace?.id ?? null,
        form,
      },
      (value) => value.isOpen
    );
  }, [editingMarketplace, form, modalOpen]);

  useEffect(() => {
    if (!modalOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeModal();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modalOpen]);

  const openAdd = () => {
    setEditingMarketplace(null);
    setForm(readStoredMarketplaceDraft() ?? emptyForm);
    setModalOpen(true);
  };

  const openEdit = (marketplace: AdminMarketplace) => {
    setEditingMarketplace(marketplace);
    setForm({
      name: marketplace.name,
      slug: marketplace.slug,
      website: marketplace.website,
      active: marketplace.active,
      entryType: marketplace.entryType,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    clearStoredMarketplaceModalState();
    setModalOpen(false);
    setEditingMarketplace(null);
    setForm(readStoredMarketplaceDraft() ?? emptyForm);
  };

  const handleBackdropMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    backdropPointerStarted.current = event.target === event.currentTarget;
  };

  const handleBackdropMouseUp = (event: React.MouseEvent<HTMLDivElement>) => {
    const shouldClose = backdropPointerStarted.current && event.target === event.currentTarget;
    backdropPointerStarted.current = false;

    if (shouldClose) {
      closeModal();
    }
  };

  const handleSave = () => {
    if (!form.name.trim()) return;

    const payload = {
      name: form.name.trim(),
      slug: form.slug.trim() || slugify(form.name),
      website: form.website.trim(),
      active: form.active,
      entryType: form.entryType,
    };

    if (editingMarketplace) {
      updateMarketplace(editingMarketplace.id, payload);
      showToast("Cadastro atualizado com sucesso!");
    } else {
      addMarketplace(payload);
      clearStoredMarketplaceDraft();
      setForm(emptyForm);
      showToast(form.entryType === "official_store" ? "Loja oficial cadastrada com sucesso!" : "Marketplace cadastrado com sucesso!");
    }

    clearStoredMarketplaceModalState();
    closeModal();
  };

  const handleDelete = () => {
    if (confirmId === null) return;
    deleteMarketplace(confirmId);
    setConfirmId(null);
    showToast("Cadastro removido.");
  };

  return (
    <div>
      <AdminHeader
        title="Marketplaces e Lojas Oficiais"
        subtitle="Cadastre separadamente os marketplaces e as lojas oficiais usadas nos produtos do catálogo"
        action={
          <div className="flex gap-3">
            <button
              onClick={() => { resetToDefault(); showToast("Lista de cadastros zerada."); }}
              className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-delete-bin-6-line mr-1.5"></i>
              Zerar Lista
            </button>
            <button
              onClick={openAdd}
              className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line mr-1.5"></i>
              Novo Cadastro
            </button>
          </div>
        }
      />

      <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700 mb-6">
        Use esta tela para manter dois catálogos separados: <strong>marketplaces</strong> para compra e <strong>lojas oficiais</strong> para identificar quem vende o produto.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-orange-50 text-orange-600">
            <i className="ri-store-2-line text-lg"></i>
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{marketplaces.length}</p>
            <p className="text-xs text-gray-500">Total de cadastros</p>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
            <i className="ri-shopping-bag-3-line text-lg"></i>
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{activeMarketplacesCount}</p>
            <p className="text-xs text-gray-500">Marketplaces ativos</p>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-sky-50 text-sky-600">
            <i className="ri-verified-badge-fill text-lg"></i>
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{activeOfficialStoresCount}</p>
            <p className="text-xs text-gray-500">Lojas oficiais ativas</p>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-violet-50 text-violet-600">
            <i className="ri-link text-lg"></i>
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{Object.values(usageByMarketplace).reduce((total, value) => total + value, 0) + Object.values(usageByOfficialStore).reduce((total, value) => total + value, 0)}</p>
            <p className="text-xs text-gray-500">Usos em produtos e ofertas</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Cadastro</th>
              <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Tipo</th>
              <th className="text-center px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Uso</th>
              <th className="text-center px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Ativo</th>
              <th className="text-right px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Ações</th>
            </tr>
          </thead>
          <tbody>
            {marketplaces.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-12 text-gray-400 text-sm">
                  <i className="ri-store-2-line text-3xl block mb-2"></i>
                  Nenhum cadastro encontrado
                </td>
              </tr>
            )}
            {marketplaces.map((marketplace) => {
              const usageCount = marketplace.entryType === "official_store"
                ? usageByOfficialStore[marketplace.name] || 0
                : usageByMarketplace[marketplace.name] || 0;

              return (
                <tr key={marketplace.id} className={`border-b border-gray-50 hover:bg-gray-50/50 transition-colors ${!marketplace.active ? "opacity-60" : ""}`}>
                  <td className="px-5 py-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-gray-800">{marketplace.name}</p>
                        <code className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-md">{marketplace.slug}</code>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap text-sm">
                        <span className="font-medium text-gray-500">Site:</span>
                        {marketplace.website ? (
                          <a
                            href={marketplace.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-orange-500 hover:underline break-all"
                          >
                            {marketplace.website}
                          </a>
                        ) : (
                          <span className="text-gray-400 italic">Não informado</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 align-top">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${marketplace.entryType === "official_store" ? "bg-sky-50 text-sky-700" : "bg-orange-50 text-orange-700"}`}>
                      <i className={marketplace.entryType === "official_store" ? "ri-verified-badge-fill" : "ri-store-2-line"}></i>
                      {getEntryTypeLabel(marketplace.entryType)}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-center align-top">
                    <span className="text-sm font-semibold text-gray-700">{usageCount}</span>
                  </td>
                  <td className="px-5 py-4 text-center align-top">
                    <button
                      onClick={() => toggleActive(marketplace.id)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer ${
                        marketplace.active ? "bg-orange-500" : "bg-gray-200"
                      }`}
                    >
                      <span
                        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                          marketplace.active ? "translate-x-4" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </td>
                  <td className="px-5 py-4 text-right align-top">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEdit(marketplace)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-orange-50 hover:text-orange-500 hover:border-orange-200 transition-colors cursor-pointer"
                      >
                        <i className="ri-edit-line text-sm"></i>
                      </button>
                      <button
                        onClick={() => setConfirmId(marketplace.id)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-red-50 hover:text-red-500 hover:border-red-200 transition-colors cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onMouseDown={handleBackdropMouseDown}
          onMouseUp={handleBackdropMouseUp}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  {editingMarketplace ? "Editar Cadastro" : "Novo Cadastro"}
                </h2>
                {!editingMarketplace && hasStoredMarketplaceDraft() && (
                  <p className="text-xs text-orange-600 mt-1">Rascunho restaurado automaticamente para continuar o cadastro.</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!editingMarketplace && hasStoredMarketplaceDraft() && (
                  <button
                    type="button"
                    onClick={() => {
                      clearStoredMarketplaceDraft();
                      setForm(emptyForm);
                    }}
                    className="px-3 py-2 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                  >
                    Limpar rascunho
                  </button>
                )}
                <button onClick={closeModal} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
            </div>

            <div className="px-6 py-5 space-y-4">
              {!editingMarketplace && (
                <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                  O cadastro salva seu progresso automaticamente enquanto você preenche. Você pode alternar de tarefa e voltar depois sem perder o rascunho.
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Tipo de cadastro</label>
                <select
                  value={form.entryType}
                  onChange={(e) => setForm((prev) => ({ ...prev, entryType: e.target.value as MarketplaceEntryType }))}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                >
                  <option value="marketplace">Marketplace</option>
                  <option value="official_store">Loja Oficial</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Loja Oficial aparece como opção separada no cadastro do produto e não é vinculada automaticamente ao marketplace.</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nome <span className="text-red-400">*</span></label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value, slug: slugify(e.target.value) }))}
                    placeholder={form.entryType === "official_store" ? "Ex: JBL Oficial" : "Ex: Amazon"}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Slug</label>
                  <input
                    type="text"
                    value={form.slug}
                    onChange={(e) => setForm((prev) => ({ ...prev, slug: slugify(e.target.value) }))}
                    placeholder={form.entryType === "official_store" ? "jbl-oficial" : "amazon"}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">URL do site</label>
                <input
                  type="url"
                  value={form.website}
                  onChange={(e) => setForm((prev) => ({ ...prev, website: e.target.value }))}
                  placeholder={form.entryType === "official_store" ? "https://loja.jbl.com.br" : "https://www.amazon.com.br"}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                />
              </div>

              <label className="flex items-center justify-between cursor-pointer rounded-xl border border-gray-100 px-4 py-3">
                <span className="text-sm font-medium text-gray-700">Disponível para uso no sistema</span>
                <button
                  type="button"
                  onClick={() => setForm((prev) => ({ ...prev, active: !prev.active }))}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer ${
                    form.active ? "bg-orange-500" : "bg-gray-200"
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      form.active ? "translate-x-4" : "translate-x-1"
                    }`}
                  />
                </button>
              </label>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {editingMarketplace ? "Salvar Alterações" : "Adicionar Cadastro"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmId !== null && (
        <ConfirmDialog
          title="Excluir Cadastro"
          message="Tem certeza que deseja excluir este cadastro? Produtos e ofertas existentes continuarão com os nomes já salvos até serem editados novamente."
          onConfirm={handleDelete}
          onCancel={() => setConfirmId(null)}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 bg-gray-900 text-white text-sm font-medium px-5 py-3 rounded-xl z-50">
          <i className="ri-checkbox-circle-line text-green-400 mr-2"></i>
          {toast}
        </div>
      )}
    </div>
  );
}